import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { MetricCard } from './shared/MetricCard';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export function HeroMetrics({
  comparisonAggregates, trendPercentages,
  googleAdsSummary, metaAdsSummary, criteoAdsSummary,
  googleAdsUnifiedData, metaAdsUnifiedData,
  pinnedMetrics, onTogglePin,
  isLoading,
}) {
  const cur = comparisonAggregates?.cur || {};
  const totalRevenue = cur.totalRevenue || 0;
  const totalSpend = (googleAdsSummary?.totalCost || 0) + (metaAdsSummary?.totalSpend || 0) + (criteoAdsSummary?.totalCost || 0);
  const blendedRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
  const sessions = cur.sessions || 0;
  const curCR = comparisonAggregates?.curCR || 0;

  // Build sparkline data from unified daily data
  const revenueSparkline = useMemo(() => {
    if (!googleAdsUnifiedData?.length) return [];
    return googleAdsUnifiedData
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .map(d => d.ga4_total_revenue || 0);
  }, [googleAdsUnifiedData]);

  const spendSparkline = useMemo(() => {
    const gData = (googleAdsUnifiedData || []).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const mData = (metaAdsUnifiedData || []).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    if (!gData.length && !mData.length) return [];
    const len = Math.max(gData.length, mData.length);
    return Array.from({ length: len }, (_, i) => (gData[i]?.cost || 0) + (mData[i]?.spend || 0));
  }, [googleAdsUnifiedData, metaAdsUnifiedData]);

  if (isLoading) return <SkeletonLoader variant="hero" />;

  const metrics = [
    { key: 'revenue', label: 'Revenue', value: formatCurrencyCompact(totalRevenue), trend: trendPercentages?.totalRevenue, sparkline: revenueSparkline, color: '#3B82F6' },
    { key: 'spend', label: 'Total Ad Spend', value: formatCurrencyCompact(totalSpend), trend: null, sparkline: spendSparkline, color: '#6366F1' },
    { key: 'roas', label: 'Blended ROAS', value: `${blendedRoas.toFixed(2)}x`, trend: null, sparkline: null, color: '#10B981' },
    { key: 'sessions', label: 'Sessions', value: formatStat(sessions), trend: trendPercentages?.sessions, sparkline: null, color: '#F59E0B' },
    { key: 'cr', label: 'Conversion Rate', value: `${curCR.toFixed(2)}%`, trend: trendPercentages?.conversionRate, sparkline: null, color: '#EC4899' },
  ];

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6"
    >
      {metrics.map((m) => (
        <motion.div key={m.key} variants={item}>
          <MetricCard
            title={m.label}
            value={m.value}
            trend={m.trend}
            sparklineData={m.sparkline}
            pinned={pinnedMetrics?.includes(m.key)}
            onPin={onTogglePin ? () => onTogglePin(m.key) : undefined}
            color={m.color}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}
